import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import type { KeyboardEvent } from 'react';
import { useId, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { useIndustryCatalog } from './industryCatalog';
import { ArrowGlyph, SectionHeading, Stage, tone } from './primitives';

export default function AgentIndustries() {
  const { t } = useLocale('chatbots');
  const baseId = useId();
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const industries = useIndustryCatalog();

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const keys = [
      'ArrowDown',
      'ArrowRight',
      'ArrowUp',
      'ArrowLeft',
      'Home',
      'End',
    ];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const last = industries.length - 1;
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? last
          : event.key === 'ArrowDown' || event.key === 'ArrowRight'
            ? (active + 1) % industries.length
            : (active - 1 + industries.length) % industries.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section id='industries' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('Ready for your industry.')}
          tail={t('Start from a use case that fits.')}
        />

        <div className='mt-14 grid gap-8 lg:grid-cols-12 lg:gap-12'>
          <div
            role='tablist'
            aria-orientation='vertical'
            aria-label={t('Industries')}
            className='-mx-6 flex gap-2 overflow-x-auto px-6 pb-2 scrollbar-hide lg:col-span-4 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0'
          >
            {industries.map(({ icon: Icon, label }, index) => {
              const selected = index === active;
              return (
                <button
                  key={label}
                  ref={(node) => {
                    tabRefs.current[index] = node;
                  }}
                  type='button'
                  role='tab'
                  id={`${baseId}-tab-${index}`}
                  aria-selected={selected}
                  aria-controls={`${baseId}-panel-${index}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(index)}
                  onKeyDown={onKeyDown}
                  className={clsxm(
                    'flex flex-none items-center gap-3 rounded-2xl px-4 py-3 text-start text-[15px] font-medium transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 lg:w-full lg:px-5 lg:py-4',
                    selected
                      ? clsxm(tone.surface, tone.line, tone.ink, 'shadow-sm')
                      : clsxm(
                          tone.body,
                          'hover:bg-white/70 dark:hover:bg-white/[0.04]',
                        ),
                  )}
                >
                  <Icon
                    className={clsxm(
                      'h-5 w-5 flex-none',
                      selected
                        ? 'text-primary-600 dark:text-primary-400'
                        : tone.quiet,
                    )}
                  />
                  <span className='whitespace-nowrap lg:whitespace-normal'>
                    {label}
                  </span>
                </button>
              );
            })}
          </div>

          <div className='lg:col-span-8'>
            {industries.map(
              ({ label, summary, question, answer, href, cta }, index) => (
                <div
                  key={label}
                  role='tabpanel'
                  id={`${baseId}-panel-${index}`}
                  aria-labelledby={`${baseId}-tab-${index}`}
                  hidden={index !== active}
                  className='h-full'
                >
                  <Stage
                    variant='lavender'
                    halftoneAt='100% 100%'
                    halftoneReach={60}
                    className='grid h-full gap-8 p-6 sm:p-10 md:grid-cols-[1fr_1.15fr] md:items-center'
                  >
                    <div className='relative'>
                      <p
                        className={clsxm(
                          'text-xl font-medium leading-8 tracking-[-0.01em]',
                          tone.ink,
                        )}
                      >
                        {summary}
                      </p>
                      <Link
                        href={href}
                        className='group mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
                      >
                        {cta}
                        <ArrowGlyph className='h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5' />
                      </Link>
                    </div>
                    <div
                      aria-hidden='true'
                      className={clsxm(
                        'relative space-y-3 rounded-2xl p-5 text-[14px] leading-relaxed shadow-[0_30px_70px_-30px_rgba(30,20,90,0.45)]',
                        tone.surface,
                        tone.line,
                      )}
                    >
                      <div className='flex justify-end'>
                        <p
                          className={clsxm(
                            'max-w-[85%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-4 py-2.5 dark:bg-white/[0.07]',
                            tone.ink,
                          )}
                        >
                          {question}
                        </p>
                      </div>
                      <p className='max-w-[88%] rounded-2xl rounded-es-md bg-primary-600 px-4 py-2.5 text-white'>
                        {answer}
                      </p>
                    </div>
                  </Stage>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
